import type { CreateReference } from '@quromedical/fhir-common';
import {
  type GroupCreateRequest,
  GroupCreateRequestSchema,
} from '@quromedical/models';
import { CrudForm, type SubmissionResult } from 'components/form';
import type { Field, PrimitiveValueType, SelectOption } from 'components/types';
import { mapReferenceToSelectOption } from 'core/forms';
import type { AsyncFetcher } from 'design-system';
import type React from 'react';
import { strings } from 'strings';

import { permissions, typeOptions } from '../config';

export type OrganizationFetcher = AsyncFetcher<PrimitiveValueType, any>;
interface FormProps {
  data: Partial<GroupCreateRequest>;
  // We're returning a full group because of validation
  handleSubmit: (
    data: GroupCreateRequest,
  ) => Promise<SubmissionResult<unknown>>;
  handleCancel?: () => void;
  organizationFetcher: OrganizationFetcher;
  title?: string;
  showSecondarySubmitButton?: boolean;
  initialOrganization?: CreateReference;
}

const schema = GroupCreateRequestSchema;

export const createFields = (
  organizationFetcher: OrganizationFetcher,
  initialOrganization?: SelectOption,
): Field<Partial<GroupCreateRequest>>[] => [
  {
    subfields: [
      {
        key: 'name',
        label: strings.LabelGroupName,
        placeholder: strings.PlaceHolderGroupName,
        type: 'text-box',
      },
    ],
  },
  {
    subfields: [
      {
        key: 'type',
        label: strings.LabelGroupType,
        initialSelection: [],
        placeholder: strings.PlaceHolderGroupType,
        type: 'combobox-single',
        fetcher: typeOptions,
      },
      {
        key: 'active',
        label: strings.LabelActive,
        placeholder: strings.PlaceHolderSelectOption,
        type: 'combobox-single',
        fetcher: [
          { display: 'True', value: true },
          { display: 'False', value: false },
        ],
      },
    ],
  },
  {
    subfields: [
      {
        type: 'combobox-multiple',
        key: 'permissions',
        label: strings.LabelPermissions,
        placeholder: strings.PlaceHolderAddPermissions,
        fetcher: permissions,
        searchable: true,
      },
      {
        type: 'combobox-single',
        key: 'organization',
        label: strings.LabelOrganization,
        searchable: true,
        initialSelection: initialOrganization ? [initialOrganization] : [],
        placeholder: strings.PlaceHolderSelectOrganization,
        fetcher: organizationFetcher,
      },
    ],
  },
];

export const Form: React.FC<FormProps> = ({
  data,
  handleCancel,
  handleSubmit,
  organizationFetcher,
  initialOrganization,
  title,
  showSecondarySubmitButton,
}) => {
  const fields = createFields(
    organizationFetcher,
    mapReferenceToSelectOption(initialOrganization),
  );
  return (
    <CrudForm
      title={title}
      validationSchema={schema}
      fields={fields}
      initialValues={data}
      onSubmit={handleSubmit}
      onSecondarySubmit={handleCancel}
      showSecondarySubmitButton={showSecondarySubmitButton}
      showSubmitButton
    />
  );
};
